/* eslint-disable react/require-default-props */
import React, { FunctionComponent, ReactNode, memo } from 'react';

import { FormField as BronsonReactFormField, ErrorMessage } from '@vwfs-bronson/bronson-react';
import { Field, FieldInputProps, FormikProps } from 'formik';
import get from 'lodash/get';

interface FormFieldProps extends React.ComponentProps<typeof BronsonReactFormField> {
  render: (props: any) => ReactNode;
  name: string;
  className?: string;
  testId?: string;
  validateOnChange?: boolean;
  /**
   * - Prop indicating if SelectFormField changed contents
   * of it's list due to eagerListRender-ing mechanics.
   */
  showEntireList?: boolean;
  inputVisualValue?: string;
  isDisabled?: boolean;
  isError?: boolean;
  isSuccess?: boolean;
  optionList?: Array<any>;
  query?: string;
  eager?: boolean;
}

interface FieldAndFormProps {
  field: FieldInputProps<any>;
  form: FormikProps<any>;
  value: FieldInputProps<any>['value'];
  fieldErrors: string;
  fieldTouched: boolean;
}

interface RenderBronsonFormFieldProps extends FormFieldProps, FieldAndFormProps {}

const getErrorMessage = (fieldTouched: boolean, errors: string, name: string) => {
  return fieldTouched && !!errors ? (
    <ErrorMessage testId={`error-message-${name}`} dangerouslySetInnerHTML={{ __html: errors as string }} />
  ) : null;
};

// eslint-disable-next-line react/display-name
const RenderBronsonFormField = memo(
  (props: RenderBronsonFormFieldProps) => {
    const { className, labelElement, labelText, field, testId, fieldErrors, fieldTouched, name, form } = props;
    const { hint, infoIcon, type, required, requiredText, render } = props;

    return (
      <BronsonReactFormField
        className={className}
        labelElement={labelElement || 'label'}
        labelText={labelText}
        type={type || 'input'}
        errorMessage={getErrorMessage(fieldTouched, fieldErrors, name)}
        hint={hint}
        infoIcon={infoIcon}
        testId={testId}
        required={required}
        requiredText={requiredText}
      >
        {render({
          ...field,
          onChange: (e) => {
            form.setFieldValue(field.name, e.target.value).then(() => form.setFieldTouched(field.name, true, false));
          },
          type,
        } as FieldInputProps<any>)}
      </BronsonReactFormField>
    );
  },
  (oldProps, newProps) => {
    if (newProps.eager) return false;
    return (
      oldProps.value === newProps.value &&
      oldProps.fieldErrors === newProps.fieldErrors &&
      oldProps.fieldTouched === newProps.fieldTouched &&
      oldProps.showEntireList === newProps.showEntireList &&
      oldProps.inputVisualValue === newProps.inputVisualValue &&
      oldProps.isDisabled === newProps.isDisabled &&
      oldProps.isError === newProps.isError &&
      oldProps.isSuccess === newProps.isSuccess &&
      oldProps.query === newProps.query &&
      oldProps.optionList === newProps.optionList
    );
  }
);

export const FormField: FunctionComponent<FormFieldProps> = (props) => (
  <Field name={props.name}>
    {(fieldAndFormProps: FieldAndFormProps) => (
      <RenderBronsonFormField
        {...props}
        {...fieldAndFormProps}
        isDisabled={props.isDisabled}
        value={fieldAndFormProps.field.value}
        fieldErrors={get(fieldAndFormProps.form.errors, fieldAndFormProps.field.name) as string}
        fieldTouched={get(fieldAndFormProps.form.touched, fieldAndFormProps.field.name) as boolean}
      />
    )}
  </Field>
);
